import React, {useEffect, useRef, useState} from 'react';
import ReactDOM from 'react-dom';
import styled from '@emotion/styled';
import {useHistory, useLocation} from 'react-router';
import useHiddenBodyOverflow from '../utils/useHiddenBodyOverflow';
import Axios from 'axios';
import {endpoint} from '../utils/api';
import auth from '../utils/auth';

/**
 * 중요 알림 팝업: 읽지 않은 운영자 공지와 문의 답변 알림을 앱·웹을 열 때 바로 보여준다.
 * (알림 메뉴에만 있으면 회원이 놓치기 쉬워서). '확인'을 누른 알림만 읽음 처리.
 * 연주 화면(/notes, /full-sheet-music)에서는 방해되지 않게 띄우지 않는다.
 */
interface IAlarm {
    id: number;
    type: 'admin' | 'inquiry';
    target_id: number;
    bold_text: string | null;
    contents: string;
    created_at: string;
}

const authHeader = () => ({Authorization: `Bearer ${auth.getToken()}`, Accept: 'application/json'});

const SKIP_PATHS = /^\/(notes|full-sheet-music|auth|login|billing)/;

const ImportantAlarm: React.FC = () => {
    const history = useHistory();
    const location = useLocation();
    const [queue, setQueue] = useState<IAlarm[]>([]);
    const checked = useRef(false); // 한 번만 조회 (상태값으로 두면 바뀌는 순간 예약한 조회가 취소됨)
    const current = queue[0];
    useHiddenBodyOverflow(!!current);

    useEffect(() => {
        if (checked.current || !auth.getToken() || SKIP_PATHS.test(location.pathname)) return;
        checked.current = true;
        setTimeout(async () => {
            try {
                // 공통 api 인스턴스를 쓰면 토큰 만료(401) 때 로그인 오류 화면으로 이동하므로, 팝업은 조용히 실패하게 따로 호출
                const {data} = await Axios.get(`${endpoint}/alarm/important`, {headers: authHeader()});
                if (data.success && data.data.length) setQueue(data.data);
            } catch (e) {
                // 알림을 못 가져와도 서비스 이용에는 지장 없게
            }
        }, 1200);
    }, [location.pathname]);

    if (!current || SKIP_PATHS.test(location.pathname)) return null;

    const done = (go?: string) => {
        Axios.post(`${endpoint}/alarm/read`, {ids: [current.id]}, {headers: authHeader()}).catch(() => null);
        const count = Number(auth.getNoticeCount() || 0);
        if (count > 0) auth.setNoticeCount(count - 1);
        setQueue(queue.slice(1));
        if (go) history.push(go);
    };

    const isInquiry = current.type === 'inquiry';
    const title = isInquiry ? '문의하신 내용에 답변이 등록되었어요' : (current.bold_text || '').replace(/[[\]]/g, '').trim() || '코드스코어 안내';
    const body = isInquiry ? `「${(current.bold_text || '').trim()}」${current.contents}` : current.contents;

    return ReactDOM.createPortal(
        <Backdrop>
            <Dialog role="dialog" aria-modal="true" aria-labelledby="important-alarm-title">
                <Badge>{isInquiry ? '문의 답변' : '공지'}{queue.length > 1 ? ` · ${queue.length}건` : ''}</Badge>
                <Title id="important-alarm-title">{title}</Title>
                <Body>{body.trim()}</Body>
                <DateText>{current.created_at.slice(0, 16)}</DateText>
                <Buttons>
                    {isInquiry && current.target_id > 0 && (
                        <Primary type="button" onClick={() => done(`/support/${current.target_id}`)}>답변 보기</Primary>
                    )}
                    {!isInquiry && /1:1\s*문의/.test(current.contents) && (
                        <Secondary type="button" onClick={() => done('/support/submit')}>1:1 문의하기</Secondary>
                    )}
                    <Primary type="button" onClick={() => done()} outline={isInquiry}>확인</Primary>
                </Buttons>
            </Dialog>
        </Backdrop>,
        document.getElementById('root') as HTMLElement,
    );
};

export default ImportantAlarm;

const Backdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1000000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  box-sizing: border-box;
  background: rgba(4, 4, 4, 0.7);
`;

const Dialog = styled.div`
  width: 100%;
  max-width: 400px;
  max-height: 100%;
  overflow-y: auto;
  padding: 24px 22px 18px;
  border-radius: 15px;
  background: white;
  color: #1e1e1e;
  word-break: keep-all;
  html[color-mode='dark'] & {
    background: #212632;
    color: #f2f2f2;
  }
`;

const Badge = styled.span`
  display: inline-block;
  margin-bottom: 10px;
  padding: 3px 10px;
  border-radius: 10px;
  background: rgba(10, 98, 186, 0.15);
  color: #0a62ba;
  font-size: 12px;
  font-weight: bold;
  html[color-mode='dark'] & {
    color: #6fb0ff;
  }
`;

const Title = styled.h2`
  margin: 0 0 12px;
  font-size: 18px;
  font-weight: bold;
  line-height: 1.4;
`;

const Body = styled.p`
  margin: 0;
  font-size: 15px;
  line-height: 1.65;
  white-space: pre-wrap;
`;

const DateText = styled.p`
  margin: 10px 0 0;
  font-size: 12px;
  opacity: 0.55;
`;

const Buttons = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 18px;
  & > button {
    flex: 1;
  }
`;

const Primary = styled.button<{ outline?: boolean }>`
  padding: 12px;
  border: 1px solid #0a62ba;
  border-radius: 8px;
  background: ${({outline}) => (outline ? 'transparent' : '#0a62ba')};
  color: ${({outline}) => (outline ? 'inherit' : 'white')};
  font-size: 15px;
  font-weight: bold;
  cursor: pointer;
`;

const Secondary = styled.button`
  padding: 12px;
  border: 1px solid rgba(128, 128, 128, 0.5);
  border-radius: 8px;
  background: transparent;
  color: inherit;
  font-size: 15px;
  cursor: pointer;
`;
